import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import styles from "./styles.module.scss";
import { useGoogleMaps } from "./hooks/useGoogleMaps";
import { useCustomDrawing } from "./hooks/useCustomDrawing";
import { use3DView } from "./hooks/use3DView";
import { useDefaultMulchProduct } from "./hooks/useDefaultMulchProduct";
import CreateRfqModal from "./components/CreateRfqModal";
import VersionHistoryModal from "./components/VersionHistoryModal";
import NudgePanel from "./components/NudgePanel";
import ExistingMapsChooserModal from "./components/ExistingMapsChooserModal";
import SelectInput from "components/forms/SelectInput";
import UserContext from "context/User";
import { useToast } from "components/toast";
import {
  translatePath,
  feetToMeters,
  OffsetMeters,
} from "./utils/geoOffset";
import { downloadJson, safeFilename } from "./utils/importExport";
import MapSnapshotsModal from "components/MapSnapshotsModal";
import { captureMapAsBlob } from "./utils/captureMap";
import {
  exportMulchMap,
} from "api/MulchMap";
import {
  createLocation,
  getLocationById,
  getMapsAtLocation,
  MapAtLocationSummary,
} from "api/Locations";
import { getClients } from "api/Clients";
import { getWorkOrder, getWorkOrders, setWorkOrderMap, updateWorkOrder } from "api/ServicePlans";
import {
  getProfilesAtLocation,
  getAssignableContacts,
  LocationClientProfileSummary,
  AssignableContact,
} from "api/LocationClientProfiles";
import {
  useEstimate,
  useMulchMap,
  useServiceAvailability,
} from "./hooks/useMulchMap";
import {
  DEFAULT_DEPTH,
  DEPTH_PRESETS,
  MAX_ZONES,
  POLY_FILL_OPACITY,
  POLY_FILL_OPACITY_HOVER,
  POLY_STROKE_OPACITY,
  POLY_STROKE_WEIGHT,
  zoneColorForIndex,
  calcCubicYards,
  formatCurrency,
  formatNumber,
  sqMetersToSqFeet,
} from "./constants";

/**
 * Phase 1: the calculator's default product is auto-detected from the
 * company's catalog (first non-addon product). Can still be overridden
 * per-environment by setting REACT_APP_DEFAULT_MULCH_PRODUCT_ID.
 * See useDefaultMulchProduct hook. Replaced by an inline product
 * selector in Phase 2.
 */

// -----------------------------------------------------------------------------
// UUID helper — crypto.randomUUID is a secure-context API, so it's available
// on HTTPS envs and on localhost. Fallback keeps older browsers unbroken.
// -----------------------------------------------------------------------------

function newMapGroupId(): string {
  const c: any = typeof window !== "undefined" ? (window as any).crypto : null;
  if (c?.randomUUID) return c.randomUUID();
  // RFC-4122 v4 fallback (good enough for a client-side session id)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// -----------------------------------------------------------------------------
// Color helper — <input type="color"> only accepts #rrggbb. Zone colors may be
// palette hex OR generated hsl() strings, so normalize to hex for the picker's
// value. Rendering (CSS + Google polygons) accepts both forms directly; this
// is only needed to seed the native color input.
// -----------------------------------------------------------------------------
function toHexColor(color: string): string {
  if (!color) return "#000000";
  const c = color.trim();
  if (c[0] === "#") {
    // Expand shorthand #rgb to #rrggbb for the native input.
    if (c.length === 4) {
      return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
    }
    return c;
  }
  const m = c.match(/hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)/i);
  if (m) {
    const h = parseFloat(m[1]) / 360;
    const s = parseFloat(m[2]) / 100;
    const l = parseFloat(m[3]) / 100;
    const hue2rgb = (p: number, q: number, t: number) => {
      let tt = t;
      if (tt < 0) tt += 1;
      if (tt > 1) tt -= 1;
      if (tt < 1 / 6) return p + (q - p) * 6 * tt;
      if (tt < 1 / 2) return q;
      if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
      return p;
    };
    let r: number;
    let g: number;
    let b: number;
    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }
    const toHex = (x: number) =>
      Math.round(x * 255)
        .toString(16)
        .padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  // Unknown format — give the picker a safe default rather than crashing.
  return "#000000";
}

// -----------------------------------------------------------------------------
// Basemap providers
// -----------------------------------------------------------------------------

type BasemapId = "google_hybrid" | "mapbox_sat" | "esri_sat" | "google_3d";

interface BasemapOption {
  id: BasemapId;
  label: string;
  attribution: string;
  // undefined = use Google's built-in "hybrid" MapTypeId. Otherwise this string
  // is the id we register via mapTypes.set() and pass to setMapTypeId().
  customTypeId?: string;
  // Actual max zoom that the underlying tile service serves.
  // The map's zoom will be clamped to this value while this basemap is active,
  // so we never request tiles the service can't fulfill.
  serviceMaxZoom: number;
}

// Google Hybrid and Mapbox both fine at 22. Esri World Imagery serves up to
// 22–23 in US metros via Maxar; we let it go to 22.
const BASEMAPS: BasemapOption[] = [
  {
    id: "google_hybrid",
    label: "Google Hybrid",
    attribution: "© Google",
    serviceMaxZoom: 22,
  },
  {
    id: "mapbox_sat",
    label: "Mapbox Satellite",
    attribution: "© Mapbox © Maxar",
    customTypeId: "mapbox_sat",
    serviceMaxZoom: 22,
  },
  {
    id: "esri_sat",
    label: "Esri World Imagery",
    attribution: "© Esri, Maxar, Earthstar",
    customTypeId: "esri_sat",
    serviceMaxZoom: 22,
  },
];

const BASEMAP_IDS: BasemapId[] = [
  "google_hybrid",
  "mapbox_sat",
  "esri_sat",
  "google_3d",
];
function isValidBasemap(v: string): v is BasemapId {
  return (BASEMAP_IDS as string[]).includes(v);
}

// -----------------------------------------------------------------------------
// Per-user basemap preference (localStorage) + zoom sweet-spots per provider
// -----------------------------------------------------------------------------

const BASEMAP_PREF_KEY = "mulch_calculator_basemap_pref_v2";

// App-wide default is Mapbox: sharper imagery, better georegistration in FL.
// User's last pick (from the picker) overrides on next session via localStorage.
// A saved mulch map's `basemap` field further overrides both.
const DEFAULT_BASEMAP: BasemapId = "mapbox_sat";

function loadBasemapPref(): BasemapId {
  try {
    const v = window.localStorage.getItem(BASEMAP_PREF_KEY);
    if (v && isValidBasemap(v)) return v as BasemapId;
  } catch {
    // ignore
  }
  return DEFAULT_BASEMAP;
}

function saveBasemapPref(b: BasemapId) {
  try {
    window.localStorage.setItem(BASEMAP_PREF_KEY, b);
  } catch {
    // ignore
  }
}

// (Previous "preferredZoomForBasemap" removed — we no longer auto-nudge zoom
// when the basemap changes. Users control zoom explicitly; polygons drawn at
// consistent zoom across providers are comparable to each other.)

function registerCustomBasemaps(map: any) {
  const g = window.google;
  if (!g?.maps) return;

  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
  if (mapboxToken) {
    map.mapTypes.set(
      "mapbox_sat",
      new g.maps.ImageMapType({
        // Mapbox's default tile response is 512px. We request the /256/
        // variant explicitly so tile dimensions match Google Maps' internal
        // 256px tile grid. Declaring both the URL and tileSize as 256 keeps
        // projection math consistent — mismatched tile sizes are a common
        // source of polygon alignment drift.
        getTileUrl: (coord: any, zoom: number) =>
          `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/256/${zoom}/${coord.x}/${coord.y}?access_token=${mapboxToken}`,
        tileSize: new g.maps.Size(256, 256),
        maxZoom: 22,
        minZoom: 0,
        name: "Mapbox Satellite",
      }),
    );
  }

  map.mapTypes.set(
    "esri_sat",
    new g.maps.ImageMapType({
      getTileUrl: (coord: any, zoom: number) =>
        `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${coord.y}/${coord.x}`,
      tileSize: new g.maps.Size(256, 256),
      maxZoom: 22,
      minZoom: 0,
      name: "Esri World Imagery",
    }),
  );
}

interface WorkingZone {
  id: string;
  name: string;
  color: string;
  depthInches: number;
  areaSqMeters: number;
  polygon: any;
  // Canonical GPS path — unaffected by display offsets. Yardage math
  // is computed from this. Stored as `path` on save.
  canonicalPath: Array<{ lat: number; lng: number }>;
  // Per-basemap visual offset (meters). Applied only at render time.
  displayOffsets: Record<string, OffsetMeters>;
}

const MapEditor = () => {
  // Staff or client. `user.role` is null for every client user — the same
  // test the Header and the trunk Quote page use for isCompany. Drives
  // CLIENT MODE below: a client measures yardage, nothing more.
  const { user } = useContext<any>(UserContext);
  const isStaff = !!user?.role;

  // -------------------------------------------------------------------------
  // Route parameters
  //
  // Canonical (Session 20):
  //   /mulch-calculator/:locationId/maps/:mapGroupId/edit
  // Alias (kept for compat with existing links + old bookmarks):
  //   /mulch-calculator/:locationId/edit  → resolves to most recent Map
  //                                          and redirects to canonical
  // Ad-hoc (new site from address search):
  //   /mulch-calculator/new/edit?address=&lat=&lng=  → findOrCreate location,
  //                                                    show chooser if any
  //                                                    Maps exist there,
  //                                                    otherwise proceed fresh
  // -------------------------------------------------------------------------
  const { locationId: routeLocationId, mapGroupId: routeMapGroupId } =
    useParams<{ locationId: string; mapGroupId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // useToast() returns { add, color, remove } directly, so we bind the
  // whole hook result as `toast` and call toast.add(message, colorName).
  const toast = useToast();

  const isNewMap = !routeLocationId || routeLocationId === "new";

  const initialAddress = searchParams.get("address") || "";
  const initialLat = Number(searchParams.get("lat")) || null;
  const initialLng = Number(searchParams.get("lng")) || null;

  // WO_MULCH_CALCULATOR_PATHWAY.md — WO-return mode. When the editor is
  // opened from a work order ("Determine with mulch calculator"), returnToWo
  // carries the WO id and clientId (optional) carries the plan's client so
  // we pre-select "Assigned to". On save we PATCH the WO's quantity to the
  // map's total cubic yards (confirm-on-differ) and navigate back to the WO.
  const returnToWoId = searchParams.get("returnToWo") || null;
  const returnClientId = searchParams.get("clientId") || null;

  // Bootstrap resolves these two — for the persistent canonical route they're
  // set synchronously from URL params, for the alias and ad-hoc routes they're
  // resolved by the boostrap effect below.
  const [resolvedLocationId, setResolvedLocationId] = useState<string | null>(
    isNewMap ? null : routeLocationId ?? null,
  );
  const [sessionMapGroupId, setSessionMapGroupId] = useState<string | null>(
    routeMapGroupId ?? null,
  );
  const [bootstrapping, setBootstrapping] = useState<boolean>(
    isNewMap || (!routeMapGroupId && !isNewMap),
  );
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  // Fallback map center resolved during bootstrap for the alias path when a
  // Location has no saved Maps yet (so there's no savedMap center and the URL
  // carries no lat/lng). Without this the map never initializes on that path.
  const [bootstrapCenter, setBootstrapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // ExistingMapsChooserModal state — populated when ad-hoc mount lands on a
  // Location that already has one or more Maps saved.
  const [chooserMaps, setChooserMaps] = useState<MapAtLocationSummary[] | null>(
    null,
  );

  const { loaded: mapsLoaded, error: mapsError } = useGoogleMaps();
  const { map: savedMap, saving, save, refetch } = useMulchMap(
    resolvedLocationId,
    sessionMapGroupId,
  );
  const {
    productId,
    loading: productLoading,
    error: productError,
  } = useDefaultMulchProduct(isStaff);
  // CLIENT MODE (Session 78). A client with the calculator enabled is
  // here to answer ONE question: how much mulch does this site need.
  // They draw zones and read the yardage. They do not price it — pricing
  // happens in the RFQ flow afterwards, with a quote they approve.
  //
  // So the three staff-only reads are not made at all rather than made
  // and stripped: `/availability` carries PROVIDER identity, `/estimate`
  // carries cost and margin, and the products list is only needed to
  // price. Passing null here is what stops them (each hook no-ops on a
  // null id), which is also why the client editor never 403s.
  const { availability } = useServiceAvailability(
    isStaff ? resolvedLocationId : null,
    productId,
  );
  const { estimate, compute, clear: clearEstimate, error: estimateError, computing: estimateComputing } = useEstimate();

  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const zonesRef = useRef<WorkingZone[]>([]);
  const zoneCounterRef = useRef(0);
  // Session 23 rewrite: gate load by savedMap.id, not a one-shot boolean.
  // A boolean ref can get stuck if the effect throws mid-loop, silently
  // losing zones with no retry. The id-based ref self-heals: if the
  // loaded map's id changes (fork, refetch of a different version), we
  // reload. Same id = no reload.
  const loadedForSavedMapIdRef = useRef<string | null>(null);
  // Session 24: snapshot modal open/close. Reads current view state off
  // mapInstanceRef when the rep clicks Capture.
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);

  const [mapReady, setMapReady] = useState(false);
  const [zones, setZones] = useState<WorkingZone[]>([]);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editNameValue, setEditNameValue] = useState("");
  const [mapName, setMapName] = useState<string>("Untitled map");
  const [editingMapName, setEditingMapName] = useState(false);
  const [mapNameDraft, setMapNameDraft] = useState<string>("");
  // Client assignment for the current Map (Session 20 Commit 5). Undefined
  // until we resolve savedMap OR the user picks explicitly; null = internal
  // draft (no client); UUID string = assigned to that client.
  const [assignedClientId, setAssignedClientId] = useState<string | null>(null);
  const [clientOptions, setClientOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  // Session 25: per-version site-contact overrides. Rep-editable in the
  // sidebar. Persisted on save, inherited from the prior current version
  // on new-version-save (backend logic). Empty string in state means
  // "clear on save"; the backend distinguishes null ("clear") from
  // undefined ("inherit").
  const [siteContactName, setSiteContactName] = useState<string>("");
  const [siteContactEmail, setSiteContactEmail] = useState<string>("");
  const [siteContactPhone, setSiteContactPhone] = useState<string>("");
  // Session 25 Batch #8: per-version primary + additional contacts on
  // the mulch_map_contacts M2M. Primary is a single Client id (or null
  // for unset); additional is a full-roster string[] the backend
  // replaces on save. Both fed by getAssignableContacts(assignedClientId),
  // so they clear whenever the map's client changes. When the client
  // is null (internal draft) the section is hidden entirely.
  const [primaryContactId, setPrimaryContactId] = useState<string | null>(
    null,
  );
  const [additionalContactIds, setAdditionalContactIds] = useState<string[]>(
    [],
  );
  const [assignableContacts, setAssignableContacts] = useState<
    AssignableContact[]
  >([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  // Phase B §7.4 — profiles at this site drive the picker's contextual
  // hint. Fetched once when locationId resolves so the rep can see whether
  // picking a client uses an existing profile or creates a new one.
  const [profilesAtSite, setProfilesAtSite] = useState<
    LocationClientProfileSummary[]
  >([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingVertexCount, setDrawingVertexCount] = useState(0);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null,
  );
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [basemap, setBasemap] = useState<BasemapId>(loadBasemapPref);
  const [basemapMenuOpen, setBasemapMenuOpen] = useState(false);
  const [rfqModalOpen, setRfqModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  // Session 27 — collapsible Contacts section, positioned below the
  // calc stack so zones/service/total lead. Collapsed by default; reps
  // expand when they need to override primary/additional/site contacts
  // for this map version. Header shows a `· N set` badge when any
  // contact-slot is populated so nothing hides silently.
  const [contactsExpanded, setContactsExpanded] = useState(false);

  // Nudge state: which zone is being adjusted, and the pending offset delta
  // (in meters) that will merge into displayOffsets[basemap] on Save.
  const [nudgingZoneId, setNudgingZoneId] = useState<string | null>(null);
  const [nudgePending, setNudgePending] = useState<OffsetMeters>({ dx: 0, dy: 0 });
  // Baseline path captured when nudge began, used to restore on Cancel.
  const nudgeBaselinePathRef = useRef<Array<{ lat: number; lng: number }> | null>(
    null,
  );

  // Persist basemap changes so the user's last choice sticks across sessions.
  // Saved-map basemaps still override on load (see effect below).
  useEffect(() => {
    saveBasemapPref(basemap);
  }, [basemap]);

  useEffect(() => {
    zonesRef.current = zones;
  }, [zones]);

  const {
    map3DContainerRef,
    is3DAvailable,
    is3DMode,
    check3DAvailability,
    toggle3D,
    syncZonesTo3D,
    updateZone3DPath,
  } = use3DView(mapInstanceRef, zonesRef, (zone: any) => {
    // 3D rendering uses the google_3d offset slot on the zone.
    const off = zone.displayOffsets?.["google_3d"] || { dx: 0, dy: 0 };
    return translatePath(zone.canonicalPath || [], off);
  });

  // -------------------------------------------------------------------------
  // Bootstrap — resolve locationId + mapGroupId per URL shape.
  //
  // Guarded by a per-URL ref so remounts / dep changes don't re-run the
  // findOrCreate + list-fetch dance.
  // -------------------------------------------------------------------------
  const bootstrappedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${routeLocationId ?? ""}::${routeMapGroupId ?? ""}::${initialLat ?? ""}::${initialLng ?? ""}::${initialAddress}`;
    if (bootstrappedKeyRef.current === key) return;
    bootstrappedKeyRef.current = key;

    // Case A — canonical: everything from the URL.
    if (routeLocationId && routeLocationId !== "new" && routeMapGroupId) {
      setResolvedLocationId(routeLocationId);
      setSessionMapGroupId(routeMapGroupId);
      setBootstrapping(false);
      return;
    }

    let cancelled = false;
    (async () => {
      // Case B — alias: locationId only. Redirect to canonical of the most
      // recently touched Map at that location; if there are no Maps, stay
      // and start a fresh one.
      if (routeLocationId && routeLocationId !== "new" && !routeMapGroupId) {
        setResolvedLocationId(routeLocationId);
        const maps = await getMapsAtLocation(routeLocationId);
        if (cancelled) return;
        if (maps.length > 0) {
          const mostRecent = maps[0]; // list comes back sorted DESC by updatedAt
          navigate(
            `/mulch-calculator/${routeLocationId}/maps/${mostRecent.mapGroupId}/edit`,
            { replace: true },
          );
          return;
        }
        // No Maps at this Location yet — treat as fresh Map on the alias URL.
        // Fetch the Location's own coords so the map can initialize; without
        // a savedMap center or URL lat/lng, initMap would otherwise have
        // nothing to center on and the editor would render blank.
        const loc: any = await getLocationById(routeLocationId);
        if (cancelled) return;
        if (loc?.latitude != null && loc?.longitude != null) {
          setBootstrapCenter({ lat: loc.latitude, lng: loc.longitude });
        } else if (
          window.google?.maps?.Geocoder &&
          (loc?.address?.line1 || loc?.name)
        ) {
          // Imported sites (e.g. the LCG batch) can have null coords — the
          // location row exists but was never geocoded. Without a center the
          // editor renders blank. Geocode the address in-memory so mapping
          // can start; the map's own center is captured on first Save.
          const addressStr = [
            loc?.address?.line1,
            loc?.address?.city,
            loc?.address?.state,
            loc?.address?.postalCode,
          ]
            .filter(Boolean)
            .join(", ") || loc?.name;
          try {
            const geocoded = await new Promise<{ lat: number; lng: number } | null>(
              (resolve) => {
                new window.google.maps.Geocoder().geocode(
                  { address: addressStr },
                  (results: any, statusCode: string) => {
                    if (statusCode === "OK" && results?.[0]) {
                      const g = results[0].geometry.location;
                      resolve({ lat: g.lat(), lng: g.lng() });
                    } else {
                      resolve(null);
                    }
                  },
                );
              },
            );
            if (cancelled) return;
            if (geocoded) setBootstrapCenter(geocoded);
          } catch {
            // Geocode failure is non-fatal — fall through; the editor will
            // surface the missing-center state rather than crash.
          }
        }
        setSessionMapGroupId(newMapGroupId());
        setBootstrapping(false);
        return;
      }

      // Case C — ad-hoc: address + coords, no locationId yet. findOrCreate,
      // then show the chooser if there are pre-existing Maps at the site.
      if (isNewMap && initialLat != null && initialLng != null) {
        const loc: any = await createLocation({
          latitude: initialLat,
          longitude: initialLng,
          address: initialAddress ? { line1: initialAddress } : {},
        });
        if (cancelled) return;
        if (!loc?.id) {
          setBootstrapError(
            loc?.message ||
              "Couldn't resolve this address. Try re-entering it from the landing page.",
          );
          setBootstrapping(false);
          return;
        }
        setResolvedLocationId(loc.id);
        const maps = await getMapsAtLocation(loc.id);
        if (cancelled) return;
        if (maps.length > 0) {
          // Editor stays disabled behind the modal until the user picks.
          setChooserMaps(maps);
          setBootstrapping(false);
          return;
        }
        // No prior Maps — start clean under a fresh mapGroupId.
        setSessionMapGroupId(newMapGroupId());
        setBootstrapping(false);
        return;
      }

      // Fallback — no route info and no coords. Send the user back to the
      // landing page rather than sit on an unusable editor.
      if (isNewMap) {
        navigate("/mulch-calculator", { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    routeLocationId,
    routeMapGroupId,
    initialLat,
    initialLng,
    initialAddress,
    isNewMap,
    navigate,
  ]);

  // Chooser modal handlers
  const handleChooserOpen = useCallback(
    (m: MapAtLocationSummary) => {
      if (!resolvedLocationId) return;
      setChooserMaps(null);
      navigate(
        `/mulch-calculator/${resolvedLocationId}/maps/${m.mapGroupId}/edit`,
      );
    },
    [navigate, resolvedLocationId],
  );

  const handleChooserStartNew = useCallback(() => {
    setChooserMaps(null);
    setSessionMapGroupId(newMapGroupId());
  }, []);

  const handleChooserCancel = useCallback(() => {
    setChooserMaps(null);
    navigate("/mulch-calculator");
  }, [navigate]);

  // -------------------------------------------------------------------------
  // Polygon listeners (used by both freshly drawn and loaded-from-server zones)
  // -------------------------------------------------------------------------

  const attachPolygonListeners = useCallback(
    (zone: WorkingZone) => {
      const path = zone.polygon.getPath();
      const updateArea = () => {
        const s = window.google.maps.geometry.spherical.computeArea(
          zone.polygon.getPath(),
        );
        // Rebuild canonicalPath from the edited polygon path by subtracting
        // the current basemap's active display offset. This keeps canonical
        // GPS coords accurate whether editing on the source basemap (offset 0)
        // or on a basemap that has a display offset.
        const editedPath = zone.polygon
          .getPath()
          .getArray()
          .map((ll: any) => ({ lat: ll.lat(), lng: ll.lng() }));
        const activeOffset = zone.displayOffsets[basemap] || { dx: 0, dy: 0 };
        const newCanonical = translatePath(editedPath, {
          dx: -activeOffset.dx,
          dy: -activeOffset.dy,
        });
        setZones((prev) =>
          prev.map((z) =>
            z.id === zone.id
              ? { ...z, areaSqMeters: s, canonicalPath: newCanonical }
              : z,
          ),
        );
        setDirty(true);
      };
      window.google.maps.event.addListener(path, "set_at", updateArea);
      window.google.maps.event.addListener(path, "insert_at", updateArea);
      window.google.maps.event.addListener(path, "remove_at", updateArea);
      window.google.maps.event.addListener(zone.polygon, "mouseover", () => {
        zone.polygon.setOptions({ fillOpacity: POLY_FILL_OPACITY_HOVER });
      });
      window.google.maps.event.addListener(zone.polygon, "mouseout", () => {
        zone.polygon.setOptions({ fillOpacity: POLY_FILL_OPACITY });
      });
    },
    [basemap],
  );

  // -------------------------------------------------------------------------
  // Map init
  // -------------------------------------------------------------------------

  const initMap = useCallback((lat: number, lng: number) => {
    if (!mapDivRef.current || !window.google) return;
    const mapId = process.env.REACT_APP_GOOGLE_MAPS_ID;

    // Vector rendering (mapId + interaction flags) unlocks 2D rotation on
    // Google Hybrid but introduces a projection mismatch with custom
    // ImageMapType overlays (Mapbox/Esri). When a custom basemap is the
    // startup default, render as raster so polygon alignment is tight. If
    // the user switches to Google Hybrid mid-session, they'll still get
    // rotation via the compass control — the map remounts on next open.
    const startingBasemap = loadBasemapPref();
    const useVector = !!(mapId && startingBasemap === "google_hybrid");

    const map = new window.google.maps.Map(mapDivRef.current, {
      ...(useVector ? { mapId } : {}),
      center: { lat, lng },
      zoom: 20,
      maxZoom: 22,
      // Disable fractional zoom on both raster and vector modes. Fractional
      // zoom is a known contributor to sub-pixel projection drift between
      // Google's polygon renderer and custom ImageMapType overlays.
      isFractionalZoomEnabled: false,
      mapTypeId: "hybrid",
      mapTypeControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      rotateControl: !!useVector,
      rotateControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      // Vector map (mapId present) needs these to enable rotate + tilt
      // drag gestures. Without them the rotate control appears but drags
      // do nothing on desktop.
      headingInteractionEnabled: !!useVector,
      tiltInteractionEnabled: !!useVector,
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: "greedy",
      clickableIcons: false,
    } as any);

    // Register custom basemap tile providers so setMapTypeId() can swap them.
    registerCustomBasemaps(map);

    new window.google.maps.Marker({ position: { lat, lng }, map });
    mapInstanceRef.current = map;
    setMapReady(true);
  }, []);

  useEffect(() => {
    if (!mapsLoaded || mapReady) return;
    // Hold off initializing the map while a chooser modal is up — the user
    // hasn't picked yet, so there's nothing to center on definitively.
    if (chooserMaps) return;
    check3DAvailability();

    if (savedMap?.mapCenterLat && savedMap?.mapCenterLng) {
      initMap(savedMap.mapCenterLat, savedMap.mapCenterLng);
    } else if (savedMap?.location?.latitude && savedMap?.location?.longitude) {
      initMap(savedMap.location.latitude, savedMap.location.longitude);
    } else if (initialLat && initialLng) {
      initMap(initialLat, initialLng);
    } else if (bootstrapCenter) {
      initMap(bootstrapCenter.lat, bootstrapCenter.lng);
    }
  }, [
    mapsLoaded,
    mapReady,
    savedMap,
    initialLat,
    initialLng,
    bootstrapCenter,
    initMap,
    check3DAvailability,
    chooserMaps,
  ]);

  // -------------------------------------------------------------------------
  // Load saved zones onto the map when both are ready
  //
  // Session 23 rewrite. Two guarantees:
  //   1. Atomic state write. Build the zone array locally, then setZones
  //      ONCE with the complete array. No incremental setZones-per-
  //      iteration — that pattern is vulnerable to state batching edge
  //      cases where later updates get dropped.
  //   2. Self-healing gate. Guard on savedMap.id, not a one-shot boolean.
  //      If the effect throws mid-loop we can retry when savedMap changes.
  // Invariant: after this effect, zones state count === savedMap.zones
  //            count, or a red toast fires and we log the shortfall.
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!mapReady || !savedMap?.zones) return;
    const savedMapId = savedMap.id;
    if (!savedMapId) return;
    if (loadedForSavedMapIdRef.current === savedMapId) return;

    // eslint-disable-next-line no-console
    console.log("loadSavedZones:incoming", {
      savedMapId,
      mapGroupId: savedMap.mapGroupId,
      version: savedMap.version,
      zoneCount: savedMap.zones.length,
      zones: savedMap.zones.map((z: any) => ({
        id: z.id,
        name: z.name,
        areaSqMeters: z.areaSqMeters,
        pathVerts: z.path?.length ?? 0,
      })),
    });

    // Build locally. If anything in this loop throws, we catch per-zone,
    // log it, and continue with the rest — rather than silently stopping
    // the loop and losing the tail.
    const built: WorkingZone[] = [];
    const skipped: Array<{ index: number; id?: string; reason: string }> = [];
    savedMap.zones.forEach((zd: any, i: number) => {
      if (!zd.path?.length) {
        skipped.push({ index: i, id: zd?.id, reason: "empty path" });
        return;
      }
      try {
        zoneCounterRef.current += 1;
        const color = zd.color || zoneColorForIndex(i);
        const canonicalPath: Array<{ lat: number; lng: number }> = zd.path.map(
          (p: any) => ({ lat: p.lat, lng: p.lng }),
        );
        const displayOffsets: Record<string, OffsetMeters> =
          zd.displayOffsets || {};
        const activeOffset = displayOffsets[basemap] || { dx: 0, dy: 0 };
        const renderedPath = translatePath(canonicalPath, activeOffset);
        const gPath = renderedPath.map(
          (p) => new window.google.maps.LatLng(p.lat, p.lng),
        );
        const polygon = new window.google.maps.Polygon({
          paths: gPath,
          map: mapInstanceRef.current,
          fillColor: color,
          fillOpacity: POLY_FILL_OPACITY,
          strokeColor: color,
          strokeOpacity: POLY_STROKE_OPACITY,
          strokeWeight: POLY_STROKE_WEIGHT,
          editable: true,
          clickable: true,
        });
        const zone: WorkingZone = {
          id: zd.id || `zone-${Date.now()}-${zoneCounterRef.current}`,
          name: zd.name || `Zone ${zoneCounterRef.current}`,
          color,
          depthInches: Number(zd.depthInches) || DEFAULT_DEPTH,
          areaSqMeters: Number(zd.areaSqMeters) || 0,
          polygon,
          canonicalPath,
          displayOffsets,
        };
        attachPolygonListeners(zone);
        built.push(zone);
      } catch (err: any) {
        skipped.push({
          index: i,
          id: zd?.id,
          reason: err?.message ?? String(err),
        });
        // eslint-disable-next-line no-console
        console.error("loadSavedZones:throw", {
          index: i,
          id: zd?.id,
          error: err?.message ?? String(err),
        });
      }
    });

    // Atomic write — replace, don't append. If a stale zone lingers in
    // state from a prior mapGroup, it's cleared here.
    setZones(built);
    // Keep the ref in sync in the same tick so anything reading
    // zonesRef between now and React's next commit sees the truth.
    zonesRef.current = built;
    loadedForSavedMapIdRef.current = savedMapId;

    // eslint-disable-next-line no-console
    console.log("loadSavedZones:done", {
      incoming: savedMap.zones.length,
      added: built.length,
      skipped: skipped.length,
      skippedDetail: skipped,
    });
    if (built.length !== savedMap.zones.length) {
      toast.add(
        `Loaded ${built.length} of ${savedMap.zones.length} zones. Do not save — saving now would drop the missing zone(s).`,
        "red",
      );
    }
  }, [mapReady, savedMap, attachPolygonListeners, basemap, toast]);

  // -------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------

  const addPolygonAsZone = useCallback(
    (path: any[]) => {
      if (!mapInstanceRef.current || path.length < 3) return;
      const zoneIndex = zonesRef.current.length;
      const color = zoneColorForIndex(zoneIndex);
      zoneCounterRef.current += 1;
      const polygon = new window.google.maps.Polygon({
        paths: path,
        map: mapInstanceRef.current,
        fillColor: color,
        fillOpacity: POLY_FILL_OPACITY,
        strokeColor: color,
        strokeOpacity: POLY_STROKE_OPACITY,
        strokeWeight: POLY_STROKE_WEIGHT,
        editable: true,
        clickable: true,
      });
      const areaSqMeters =
        window.google.maps.geometry.spherical.computeArea(polygon.getPath());
      const canonicalPath = polygon
        .getPath()
        .getArray()
        .map((ll: any) => ({ lat: ll.lat(), lng: ll.lng() }));
      const zone: WorkingZone = {
        id: `zone-${Date.now()}-${zoneCounterRef.current}`,
        name: `Zone ${zoneCounterRef.current}`,
        color,
        depthInches: DEFAULT_DEPTH,
        areaSqMeters,
        polygon,
        canonicalPath,
        displayOffsets: {},
      };
      attachPolygonListeners(zone);
      setZones((prev) => [...prev, zone]);
      setIsDrawing(false);
      setDrawingVertexCount(0);
      setDirty(true);
    },
    [attachPolygonListeners],
  );

  const { startDrawing, stopDrawing, undoLastVertex, onUndo } =
    useCustomDrawing(mapInstanceRef, addPolygonAsZone);

  useEffect(() => {
    const unsub = onUndo((count) => {
      if (count === -1) {
        setIsDrawing(false);
        setDrawingVertexCount(0);
      } else {
        setDrawingVertexCount(count);
      }
    });
    return unsub;
  }, [onUndo]);

  const beginDraw = () => {
    if (!mapReady || zones.length >= MAX_ZONES || is3DMode) return;
    const nextColor = zoneColorForIndex(zones.length);
    zones.forEach((z) => z.polygon?.setEditable(false));
    startDrawing(nextColor);
    setIsDrawing(true);
    setDrawingVertexCount(0);
  };

  const cancelDraw = () => {
    stopDrawing();
    setIsDrawing(false);
    setDrawingVertexCount(0);
    zones.forEach((z) => z.polygon?.setEditable(true));
  };

  // -------------------------------------------------------------------------
  // Zone actions
  // -------------------------------------------------------------------------

  const deleteZone = (id: string) => {
    setZones((prev) => {
      const zone = prev.find((z) => z.id === id);
      if (zone?.polygon) zone.polygon.setMap(null);
      return prev.filter((z) => z.id !== id);
    });
    setDirty(true);
  };

  const updateDepth = (id: string, depth: number) => {
    setZones((prev) =>
      prev.map((z) => (z.id === id ? { ...z, depthInches: depth } : z)),
    );
    setDirty(true);
  };

  // Manual per-zone color override. Repaints the polygon live (fill +
  // stroke) and persists via the zone's `color` field on save and export.
  const updateZoneColor = (id: string, color: string) => {
    setZones((prev) =>
      prev.map((z) => {
        if (z.id !== id) return z;
        if (z.polygon) {
          z.polygon.setOptions({ fillColor: color, strokeColor: color });
        }
        return { ...z, color };
      }),
    );
    setDirty(true);
  };

  const startEditName = (zone: WorkingZone) => {
    setEditingNameId(zone.id);
    setEditNameValue(zone.name);
  };

  const finishEditName = (id: string) => {
    if (editNameValue.trim()) {
      setZones((prev) =>
        prev.map((z) =>
          z.id === id ? { ...z, name: editNameValue.trim() } : z,
        ),
      );
      setDirty(true);
    }
    setEditingNameId(null);
  };

  // Adopt the saved map's name whenever we load or switch Maps. If the saved
  // row hasn't been named yet (legacy row backfilled as "Original", or fresh
  // v1 defaulted to "Untitled map"), we still surface that as the editable
  // starting value — changing it and saving writes the new name to v(N+1).
  //
  // Effect only takes an action when either (a) a savedMap arrives with a
  // name, or (b) no savedMap AND no unsaved edits. So `dirty` flipping true
  // during typing hits the else branch and no-ops, which is what we want.
  useEffect(() => {
    if (savedMap?.name) {
      setMapName(savedMap.name);
    } else if (!savedMap && !dirty) {
      // Fresh Map with no saved row yet — start blank-ish.
      setMapName("Untitled map");
    }
  }, [savedMap?.id, savedMap?.name, savedMap, dirty]);

  const commitMapNameEdit = () => {
    const next = mapNameDraft.trim();
    setEditingMapName(false);
    if (!next) return;
    if (next === mapName) return;
    setMapName(next);
    setDirty(true);
  };

  // Load client options once on mount so the Client picker in the sidebar
  // can bind. We use recordsPer=500 to fit the vast majority of catalogs
  // without paging — large enough for GetMulch's ~284 active clients with
  // headroom, small enough not to hammer the backend on every editor open.
  //
  // STAFF ONLY (Session 78): `/clients` is staff-scoped, so for a client
  // this was a guaranteed 403 on every editor open — harmless (the list
  // came back empty and the picker is hidden in client mode) but a
  // pointless request and console noise on a screen we want clean.
  useEffect(() => {
    if (!isStaff) {
      setClientOptions([]);
      setClientsLoading(false);
      return;
    }
    let cancelled = false;
    setClientsLoading(true);
    getClients({ query: { recordsPer: 500 } }).then((res: any) => {
      if (cancelled) return;
      const items = Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res)
          ? res
          : [];
      setClientOptions(
        items.map((c: any) => ({
          value: c.id,
          label:
            c.name ||
            c.companyName ||
            c.primaryUser?.email ||
            c.id,
        })),
      );
      setClientsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isStaff]);

  // Sync the picker to the loaded Map's assignment. When the user switches
  // between Maps (canonical URL change), this pulls in the new one's client.
  // Session 26: previously had `dirty` in deps + an `else if (!dirty)` clear
  // branch. The `dirty` dep caused the effect to fire when a rep first
  // edited anything, stomping their pick with the stale savedMap value.
  // Dropped both — the effect now runs only on savedMap identity changes.
  useEffect(() => {
    if (savedMap) {
      setAssignedClientId(savedMap.client?.id ?? null);
    }
  }, [savedMap?.id, savedMap?.client?.id, savedMap]);

  // WO-return mode: seed "Assigned to" from the plan's client passed in the
  // URL, once, on a fresh map that has no saved assignment yet. A loaded map
  // with its own client still wins (the savedMap effect above runs too, but
  // only sets from savedMap.client which is null on a fresh map). Guarded by
  // a ref so re-renders don't stomp a subsequent manual change by the rep.
  const seededReturnClientRef = useRef(false);
  useEffect(() => {
    if (seededReturnClientRef.current) return;
    if (!returnClientId) return;
    // Only seed when there's no saved map yet (fresh measure) or the saved
    // map has no client. Never override a client already on a loaded map.
    if (savedMap?.client?.id) {
      seededReturnClientRef.current = true;
      return;
    }
    setAssignedClientId(returnClientId);
    seededReturnClientRef.current = true;
  }, [returnClientId, savedMap?.client?.id, savedMap]);

  // WO-return mode: the WO's current quantity, fetched once so that on save
  // we can confirm-on-differ before overwriting it with the map total
  // (WO_MULCH_CALCULATOR_PATHWAY.md, open question 2). null = unknown / not
  // fetched; "" or a numeric string once known.
  const [returnWoQty, setReturnWoQty] = useState<string | null>(null);
  useEffect(() => {
    if (!returnToWoId) return;
    let cancelled = false;
    getWorkOrder({ data: { id: returnToWoId } }).then((woRow: any) => {
      if (cancelled) return;
      setReturnWoQty(
        woRow?.quantity != null && woRow?.quantity !== "" ? String(woRow.quantity) : "",
      );
    });
    return () => {
      cancelled = true;
    };
  }, [returnToWoId]);

  // KNOWN_ISSUES #17 — surface the work order(s) at this site.
  //
  // The banner below this one only appears when the calculator was OPENED
  // from a work order (`?returnToWo=`). Arriving any other way — from the
  // location page, from the map list, from a saved link — gave no hint that
  // live work exists here, so someone could re-measure a site that already
  // has an open work order and never know to go settle it.
  //
  // Uses the EXISTING `locationId` filter on the WO list; no backend change.
  // The list already leftJoins `wo.mulchMap`, so the map match is done here
  // rather than with a new query param.
  //
  // Two tiers, deliberately ordered: a WO linked to THIS map is the exact
  // hit and gets named; otherwise any live WO at the site is worth knowing
  // about. Rejected WOs are excluded — dead work is not a prompt.
  const [siteWorkOrders, setSiteWorkOrders] = useState<any[]>([]);
  useEffect(() => {
    if (!resolvedLocationId || returnToWoId) {
      setSiteWorkOrders([]);
      return;
    }
    let cancelled = false;
    getWorkOrders({ query: { locationId: resolvedLocationId } })
      .then((res: any) => {
        if (cancelled) return;
        const rows = Array.isArray(res) ? res : res?.items ?? [];
        setSiteWorkOrders(rows.filter((w: any) => w?.status !== "rejected"));
      })
      .catch(() => {
        // Non-fatal: this is an advisory banner, never a gate on measuring.
        if (!cancelled) setSiteWorkOrders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedLocationId, returnToWoId]);

  // The WO linked to the map currently open, if any. Matches on the map
  // VERSION row id and on the mapGroup, because a WO pins a specific
  // version while the editor may be on a newer one in the same group.
  const linkedWorkOrder = useMemo(() => {
    if (!siteWorkOrders.length) return null;
    return (
      siteWorkOrders.find(
        (w: any) =>
          (savedMap?.id && w?.mulchMap?.id === savedMap.id) ||
          (sessionMapGroupId && w?.mulchMap?.mapGroupId === sessionMapGroupId),
      ) ?? null
    );
  }, [siteWorkOrders, savedMap?.id, sessionMapGroupId]);

  // WO-return mode: after a normal map save succeeds, PATCH the WO's quantity
  // to the map total and navigate back to the WO. Confirm-on-differ when the
  // WO already carried a different quantity; silent when it was empty. Called
  // from handleSave's success paths in place of the canonical redirect.
  //
  // KNOWN_ISSUES #15 — this used to push the QUANTITY back and nothing else,
  // so a map measured from a work order ended up attached to the LOCATION
  // only. The WO got the number but no `mulchMapId`, which is the field the
  // case file's "priced from" panel and the plan/queue map-status chip both
  // read. `savedMapId` is the saved VERSION row id (`res.id` from the save),
  // which is the same thing `useMapTotal` on the WO case file pins via
  // `m.currentMapId` — not the mapGroupId.
  //
  // The link is written on BOTH branches, including when the operator
  // DECLINES the quantity overwrite. Measuring from a work order is itself
  // the statement that this map belongs to it; declining the overwrite is a
  // statement about the NUMBER, not about which map is the site's. Linking
  // is also reversible from the case file's Unlink affordance, whereas
  // silently not linking leaves no trace that a measurement happened.
  //
  // Non-fatal: a link failure must never lose the map or the quantity, both
  // of which are already saved by this point.
  const returnToWorkOrderAfterSave = async (
    savedMapId?: string | null,
  ): Promise<boolean> => {
    if (!returnToWoId) return false;

    const linkMapToWo = async () => {
      if (!savedMapId) return;
      try {
        await setWorkOrderMap({
          data: { id: returnToWoId },
          mulchMapId: savedMapId,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("returnToWorkOrderAfterSave: map link failed", err);
        toast.add(
          "Saved the map, but could not link it to the work order.",
          "orange",
        );
      }
    };

    const mapTotal = Math.round(totalCuYd * 100) / 100;
    const prior = returnWoQty;
    const priorNum = prior != null && prior !== "" ? Number(prior) : null;
    if (priorNum != null && priorNum !== mapTotal) {
      const ok = window.confirm(
        `This work order already has a quantity of ${priorNum}. Replace it with the measured total of ${mapTotal} cu yd?`,
      );
      if (!ok) {
        // Operator declined the overwrite. Stay on the calculator; the map is
        // saved either way. They can navigate back manually without a write.
        await linkMapToWo();
        toast.add("Kept the work order's existing quantity. The map is saved and linked.", "orange");
        return true;
      }
    }
    const res = await updateWorkOrder({
      data: { id: returnToWoId },
      update: { quantity: mapTotal },
    });
    if (!res?.id) {
      await linkMapToWo();
      toast.add(res?.message || "Saved the map, but could not update the work order quantity.", "red");
      return true;
    }
    await linkMapToWo();
    toast.add(`Work order quantity set to ${mapTotal} cu yd from the map, and the map linked.`, "green");
    navigate(`/maintenance/work-orders/${returnToWoId}`);
    return true;
  };

  // Session 25: hydrate site-contact fields from the loaded map version.
  // Session 26: same dirty-dep bug as assignedClientId — fixed identically.
  useEffect(() => {
    if (savedMap) {
      setSiteContactName((savedMap as any).siteContactName ?? "");
      setSiteContactEmail((savedMap as any).siteContactEmail ?? "");
      setSiteContactPhone((savedMap as any).siteContactPhone ?? "");
    }
  }, [
    savedMap?.id,
    (savedMap as any)?.siteContactName,
    (savedMap as any)?.siteContactEmail,
    (savedMap as any)?.siteContactPhone,
    savedMap,
  ]);

  // Session 25 Batch #8: hydrate per-version primaryContactId +
  // additionalContactIds from savedMap. Fires only on savedMap identity
  // or its contact-relation shape changes. `dirty` deliberately absent
  // from the deps so a rep's first pick doesn't get stomped by a re-run
  // of the effect — that was the Session 26 test failure Jack surfaced.
  // contacts is a small M2M array so hashing the ids into a stable string
  // keeps the dep list stable across parent re-renders.
  const savedContactIdsKey = useMemo(() => {
    const list = (savedMap as any)?.contacts ?? [];
    return Array.isArray(list)
      ? list
          .map((c: any) => c?.id)
          .filter(Boolean)
          .join(",")
      : "";
  }, [savedMap]);
  useEffect(() => {
    if (savedMap) {
      setPrimaryContactId((savedMap as any).primaryClient?.id ?? null);
      const contacts = ((savedMap as any).contacts ?? []) as Array<{
        id: string;
      }>;
      setAdditionalContactIds(
        Array.isArray(contacts) ? contacts.map((c) => c.id) : [],
      );
    }
  }, [
    savedMap?.id,
    (savedMap as any)?.primaryClient?.id,
    savedContactIdsKey,
    savedMap,
  ]);

  // Session 25 Batch #8: fetch the assignable contacts roster whenever
  // the map's assigned client changes. Backend scopes to the client's
  // org children (or the client itself when it's an individual customer
  // with no children), so nothing cross-org can ever appear in the
  // pickers. Cleared to [] when the client is null.
  useEffect(() => {
    if (!assignedClientId) {
      setAssignableContacts([]);
      setContactsLoading(false);
      return;
    }
    let cancelled = false;
    setContactsLoading(true);
    getAssignableContacts(assignedClientId).then((res) => {
      if (cancelled) return;
      setAssignableContacts(Array.isArray(res) ? res : []);
      setContactsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [assignedClientId]);

  // Phase B §7.4 — fetch profiles at this site so the Client picker can
  // show "picking [Client] uses existing profile" vs "creates new profile"
  // hints. Cheap query (indexed on locationId). Refetched only when the
  // site changes.
  useEffect(() => {
    if (!resolvedLocationId || !isStaff) {
      setProfilesAtSite([]);
      return;
    }
    let cancelled = false;
    getProfilesAtLocation(resolvedLocationId).then((res) => {
      if (cancelled) return;
      setProfilesAtSite(Array.isArray(res) ? res : []);
    });
    return () => {
      cancelled = true;
    };
  }, [resolvedLocationId]);

  // Does the currently-picked client already have a profile at this site?
  const matchingProfile = useMemo(() => {
    if (!assignedClientId) return null;
    return (
      profilesAtSite.find((p) => p.clientId === assignedClientId) ?? null
    );
  }, [assignedClientId, profilesAtSite]);

  // Sync 3D polygons when zones change while in 3D
  useEffect(() => {
    if (is3DMode) syncZonesTo3D();
  }, [zones, is3DMode, syncZonesTo3D]);

  // Adopt the saved basemap when a persisted map is opened. This runs after
  // the localStorage pref is loaded, so a saved map's basemap always wins
  // over the user's default when viewing an existing map.
  useEffect(() => {
    if (savedMap?.basemap && isValidBasemap(savedMap.basemap)) {
      setBasemap(savedMap.basemap as BasemapId);
    }
  }, [savedMap?.basemap]);

  // Apply basemap when the selection changes and the map is ready. Only
  // clamp zoom downward if the new provider can't serve the current zoom.
  // We don't nudge zoom UP anymore — forcing users to different zooms per
  // provider makes cross-provider polygon comparisons unreliable (draw on
  // Mapbox at 21, switch to Google at 20, polygons look scaled down because
  // your pixel precision was 2x tighter when drawing).
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    const opt = BASEMAPS.find((b) => b.id === basemap);
    if (!opt) return;
    const map = mapInstanceRef.current;
    if (opt.customTypeId) {
      map.setMapTypeId(opt.customTypeId);
    } else {
      map.setMapTypeId("hybrid");
    }
    map.setOptions({ maxZoom: opt.serviceMaxZoom });
    const currentZoom = typeof map.getZoom === "function" ? map.getZoom() : 20;
    if (currentZoom > opt.serviceMaxZoom) {
      map.setZoom(opt.serviceMaxZoom);
    }

    // Re-render each polygon's path with the new basemap's display offset.
    // Canonical GPS coords are untouched — only the visual position shifts.
    zonesRef.current.forEach((zone) => {
      const activeOffset = zone.displayOffsets[basemap] || { dx: 0, dy: 0 };
      const renderedPath = translatePath(zone.canonicalPath, activeOffset);
      zone.polygon.setPath(
        renderedPath.map(
          (p) => new window.google.maps.LatLng(p.lat, p.lng),
        ),
      );
    });
  }, [basemap, mapReady]);

  // -------------------------------------------------------------------------
  // Nudge mode
  // -------------------------------------------------------------------------

  // The effective basemap for nudge purposes. In 3D mode this is
  // "google_3d" regardless of what the 2D picker says.
  const activeBasemap: BasemapId = is3DMode ? "google_3d" : basemap;

  // Nudge is available on any basemap. Adjustments apply to the current
  // basemap only — canonical GPS coords are unchanged. Users can align
  // polygons visually against any provider's imagery independently.
  const canNudge = true;

  const nudgingZone = zones.find((z) => z.id === nudgingZoneId) || null;

  // Apply pendingOffset live to the nudging polygon
  useEffect(() => {
    if (!nudgingZone) return;
    const base = zones.find((z) => z.id === nudgingZone.id);
    if (!base) return;
    const savedOffset = base.displayOffsets[activeBasemap] || { dx: 0, dy: 0 };
    const totalOffset = {
      dx: savedOffset.dx + nudgePending.dx,
      dy: savedOffset.dy + nudgePending.dy,
    };
    const rendered = translatePath(base.canonicalPath, totalOffset);
    if (is3DMode) {
      updateZone3DPath(base.id, rendered);
    } else {
      base.polygon.setPath(
        rendered.map((p) => new window.google.maps.LatLng(p.lat, p.lng)),
      );
    }
  }, [nudgePending, nudgingZone, activeBasemap, is3DMode, updateZone3DPath, zones]);

  const startNudge = (zoneId: string) => {
    const zone = zones.find((z) => z.id === zoneId);
    if (!zone) return;
    // Capture baseline for Cancel restore. In 3D, baseline is the current
    // 3D-offset-applied path derived from canonical. In 2D, the polygon's
    // current path is baseline.
    if (is3DMode) {
      const off = zone.displayOffsets["google_3d"] || { dx: 0, dy: 0 };
      nudgeBaselinePathRef.current = translatePath(zone.canonicalPath, off);
    } else {
      nudgeBaselinePathRef.current = zone.polygon
        .getPath()
        .getArray()
        .map((ll: any) => ({ lat: ll.lat(), lng: ll.lng() }));
    }
    // Disable vertex editing while nudging; enable rigid drag on 2D only
    // (3D polygons don't support drag — arrow keys only in 3D).
    if (!is3DMode) {
      zones.forEach((z) => {
        if (z.id === zoneId) {
          z.polygon.setEditable(false);
          z.polygon.setDraggable(true);
          z.polygon.setOptions({
            strokeOpacity: 1,
            strokeWeight: POLY_STROKE_WEIGHT + 1,
            fillOpacity: POLY_FILL_OPACITY_HOVER,
          });
        } else {
          z.polygon.setEditable(false);
          z.polygon.setOptions({ fillOpacity: 0.1, strokeOpacity: 0.4 });
        }
      });
    }
    setNudgePending({ dx: 0, dy: 0 });
    setNudgingZoneId(zoneId);
  };

  const exitNudgeVisuals = () => {
    zones.forEach((z) => {
      z.polygon.setDraggable(false);
      z.polygon.setEditable(true);
      z.polygon.setOptions({
        strokeOpacity: POLY_STROKE_OPACITY,
        strokeWeight: POLY_STROKE_WEIGHT,
        fillOpacity: POLY_FILL_OPACITY,
      });
    });
  };

  const cancelNudge = () => {
    if (!nudgingZone || !nudgeBaselinePathRef.current) {
      exitNudgeVisuals();
      setNudgingZoneId(null);
      setNudgePending({ dx: 0, dy: 0 });
      return;
    }
    // Restore polygon to baseline path (2D or 3D)
    if (is3DMode) {
      updateZone3DPath(nudgingZone.id, nudgeBaselinePathRef.current);
    } else {
      nudgingZone.polygon.setPath(
        nudgeBaselinePathRef.current.map(
          (p) => new window.google.maps.LatLng(p.lat, p.lng),
        ),
      );
    }
    nudgeBaselinePathRef.current = null;
    exitNudgeVisuals();
    setNudgingZoneId(null);
    setNudgePending({ dx: 0, dy: 0 });
  };

  const saveNudge = () => {
    if (!nudgingZone) return;
    const prev = nudgingZone.displayOffsets[activeBasemap] || { dx: 0, dy: 0 };
    const merged = {
      dx: prev.dx + nudgePending.dx,
      dy: prev.dy + nudgePending.dy,
    };
    setZones((zs) =>
      zs.map((z) =>
        z.id === nudgingZone.id
          ? {
              ...z,
              displayOffsets: { ...z.displayOffsets, [activeBasemap]: merged },
            }
          : z,
      ),
    );
    setDirty(true);
    nudgeBaselinePathRef.current = null;
    exitNudgeVisuals();
    setNudgingZoneId(null);
    setNudgePending({ dx: 0, dy: 0 });
  };

  const resetNudge = () => {
    if (!nudgingZone) return;
    // Reset both the pending delta AND the saved offset for the active basemap
    setZones((zs) =>
      zs.map((z) => {
        if (z.id !== nudgingZone.id) return z;
        const next = { ...z.displayOffsets };
        delete next[activeBasemap];
        return { ...z, displayOffsets: next };
      }),
    );
    setNudgePending({ dx: 0, dy: 0 });
    setDirty(true);
    // Redraw with zero offset
    if (is3DMode) {
      updateZone3DPath(nudgingZone.id, nudgingZone.canonicalPath);
    } else {
      nudgingZone.polygon.setPath(
        nudgingZone.canonicalPath.map(
          (p) => new window.google.maps.LatLng(p.lat, p.lng),
        ),
      );
    }
  };

  // Detect polygon drag: when nudge is active, dragend gives us the delta
  useEffect(() => {
    if (!nudgingZone) return;
    const listener = window.google.maps.event.addListener(
      nudgingZone.polygon,
      "dragend",
      () => {
        const newPath = nudgingZone.polygon
          .getPath()
          .getArray()
          .map((ll: any) => ({ lat: ll.lat(), lng: ll.lng() }));
        const savedOffset = nudgingZone.displayOffsets[basemap] || { dx: 0, dy: 0 };
        const expected = translatePath(nudgingZone.canonicalPath, {
          dx: savedOffset.dx + nudgePending.dx,
          dy: savedOffset.dy + nudgePending.dy,
        });
        // Compute lat/lng delta from expected to newPath (using first point)
        if (!expected.length || !newPath.length) return;
        const dLat = newPath[0].lat - expected[0].lat;
        const dLng = newPath[0].lng - expected[0].lng;
        // Convert back to meters
        const METERS_PER_DEG_LAT = 111320;
        const dyM = dLat * METERS_PER_DEG_LAT;
        const dxM =
          dLng *
          METERS_PER_DEG_LAT *
          Math.cos((expected[0].lat * Math.PI) / 180);
        setNudgePending((prev) => ({
          dx: prev.dx + dxM,
          dy: prev.dy + dyM,
        }));
      },
    );
    return () => window.google.maps.event.removeListener(listener);
  }, [nudgingZone, basemap, nudgePending]);

  // Keyboard arrows during nudge: 1 ft per step, 5 ft with shift.
  // Handlers are stashed in a ref so the effect subscribes once and always
  // sees the latest saveNudge/cancelNudge without re-subscribing.
  const nudgeActionsRef = useRef<{
    save: () => void;
    cancel: () => void;
  } | null>(null);
  nudgeActionsRef.current = { save: saveNudge, cancel: cancelNudge };

  useEffect(() => {
    if (!nudgingZoneId) return;
    const handler = (e: KeyboardEvent) => {
      const stepFt = e.shiftKey ? 5 : 1;
      const stepM = feetToMeters(stepFt);
      let dx = 0;
      let dy = 0;
      switch (e.key) {
        case "ArrowUp":
          dy = stepM;
          break;
        case "ArrowDown":
          dy = -stepM;
          break;
        case "ArrowRight":
          dx = stepM;
          break;
        case "ArrowLeft":
          dx = -stepM;
          break;
        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          nudgeActionsRef.current?.save();
          return;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          nudgeActionsRef.current?.cancel();
          return;
        default:
          return;
      }
      e.preventDefault();
      e.stopPropagation();
      setNudgePending((prev) => ({ dx: prev.dx + dx, dy: prev.dy + dy }));
    };
    window.addEventListener("keydown", handler, { capture: true });
    return () =>
      window.removeEventListener("keydown", handler, { capture: true });
  }, [nudgingZoneId]);

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------

  const zoneRollup = useMemo(
    () =>
      zones.map((z) => {
        const areaSqFt = sqMetersToSqFeet(z.areaSqMeters);
        return {
          ...z,
          areaSqFt,
          cubicYards: calcCubicYards(areaSqFt, z.depthInches),
        };
      }),
    [zones],
  );

  const totalSqFt = zoneRollup.reduce((s, z) => s + z.areaSqFt, 0);
  const totalCuYd = zoneRollup.reduce((s, z) => s + z.cubicYards, 0);

  useEffect(() => {
    if (
      !selectedServiceId &&
      availability?.services?.length &&
      availability.services[0]?.id
    ) {
      setSelectedServiceId(availability.services[0].id);
    }
  }, [availability, selectedServiceId]);

  const availableAddOns = useMemo(
    () =>
      selectedServiceId && availability
        ? availability.addOnsPerService[selectedServiceId] || []
        : [],
    [availability, selectedServiceId],
  );

  useEffect(() => {
    if (!selectedAddOnIds.length) return;
    const availIds = new Set(availableAddOns.map((a) => a.id));
    const filtered = selectedAddOnIds.filter((id) => availIds.has(id));
    if (filtered.length !== selectedAddOnIds.length) {
      setSelectedAddOnIds(filtered);
    }
  }, [availableAddOns, selectedAddOnIds]);

  useEffect(() => {
    if (
      !isStaff ||
      !resolvedLocationId ||
      !productId ||
      !selectedServiceId ||
      !zones.length
    ) {
      clearEstimate();
      return;
    }
    const handle = setTimeout(() => {
      compute({
        locationId: resolvedLocationId,
        productId,
        serviceId: selectedServiceId,
        addOnIds: selectedAddOnIds,
        zones: zones.map((z) => ({
          depthInches: z.depthInches,
          areaSqMeters: z.areaSqMeters,
        })),
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [
    resolvedLocationId,
    productId,
    selectedServiceId,
    selectedAddOnIds,
    zones,
    compute,
    clearEstimate,
  ]);

  // -------------------------------------------------------------------------
  // Save
  //
  // Session 20 semantics:
  //   - locationId is resolved at mount time (canonical URL, alias lookup, or
  //     ad-hoc findOrCreate). No more silent createLocation-on-save.
  //   - Every save carries the current session's mapGroupId. Fresh Maps got
  //     it from newMapGroupId() during bootstrap; opened Maps got it from
  //     the URL. Backend uses this to demote isCurrent within the group only.
  //   - After a fresh Map's first save (URL was alias or ad-hoc), we redirect
  //     to the canonical URL so bookmarks and back-navigation stay stable.
  // -------------------------------------------------------------------------

  const handleSave = async () => {
    if (!resolvedLocationId || !sessionMapGroupId) return;
    if (totalCuYd === 0) return;

    const center = mapInstanceRef.current?.getCenter?.();
    const zoom = mapInstanceRef.current?.getZoom?.();

    const sentZoneCount = zones.length;

    // Session 23 rewrite: pre-save invariants. Refuse to send anything if
    // state and its mirror ref disagree, or if any zone lacks a polygon /
    // canonical path. Better to abort with a clear message than to
    // silently save partial data.
    if (zonesRef.current.length !== zones.length) {
      // eslint-disable-next-line no-console
      console.error("handleSave:state-ref-mismatch", {
        stateCount: zones.length,
        refCount: zonesRef.current.length,
      });
      toast.add(
        `Internal error: zone state is out of sync (${zones.length} vs ${zonesRef.current.length}). Reload before saving.`,
        "red",
      );
      return;
    }
    const badZoneIndex = zones.findIndex(
      (z) => !z.polygon || !Array.isArray(z.canonicalPath) || z.canonicalPath.length < 3,
    );
    if (badZoneIndex >= 0) {
      // eslint-disable-next-line no-console
      console.error("handleSave:malformed-zone", {
        index: badZoneIndex,
        zone: zones[badZoneIndex],
      });
      toast.add(
        `Zone ${badZoneIndex + 1} is malformed. Delete and redraw it before saving.`,
        "red",
      );
      return;
    }

    const payloadZones = zones.map((z, i) => ({
      name: z.name,
      color: z.color,
      depthInches: z.depthInches,
      areaSqMeters: z.areaSqMeters,
      // Save canonical GPS coords, not the display-offset polygon path
      path: z.canonicalPath,
      displayOffsets: z.displayOffsets,
      sortOrder: i,
    }));

    // Session 23: post-build invariant. If the payload count doesn't
    // match the state count something is deeply wrong with map/forEach.
    if (payloadZones.length !== sentZoneCount) {
      // eslint-disable-next-line no-console
      console.error("handleSave:payload-shrink", {
        stateCount: sentZoneCount,
        payloadCount: payloadZones.length,
      });
      toast.add(
        `Internal error: payload lost zones (${sentZoneCount} → ${payloadZones.length}). Save aborted.`,
        "red",
      );
      return;
    }

    // eslint-disable-next-line no-console
    console.log("handleSave:payload", {
      resolvedLocationId,
      sessionMapGroupId,
      routeMapGroupId,
      sentZoneCount,
      savedMapVersion: (savedMap as any)?.version,
      savedMapId: (savedMap as any)?.id,
      zones: payloadZones.map((z) => ({
        name: z.name,
        color: z.color,
        pathVerts: z.path?.length ?? 0,
        areaSqMeters: z.areaSqMeters,
        sortOrder: z.sortOrder,
      })),
    });

    const res = await save({
      locationId: resolvedLocationId,
      mapGroupId: sessionMapGroupId,
      name: mapName,
      // Explicit null = detach client; UUID = assign; undefined would inherit,
      // but the picker always resolves to one of these two so we send both.
      clientId: assignedClientId,
      // Session 25: send explicit null when the rep cleared the field,
      // explicit string when they typed one. undefined would inherit
      // from prior version — not what we want after a rep edit.
      siteContactName: siteContactName.trim() === "" ? null : siteContactName.trim(),
      siteContactEmail: siteContactEmail.trim() === "" ? null : siteContactEmail.trim(),
      siteContactPhone: siteContactPhone.trim() === "" ? null : siteContactPhone.trim(),
      // Session 25 Batch #8: primary + additional contacts. Same explicit
      // semantics as clientId — picker state is always a concrete null or
      // string / string[], so we send both fields on every save rather
      // than let the backend inherit from prior version. When no client
      // is assigned the pickers are hidden and both values stay null / [].
      primaryClientId: primaryContactId,
      contactIds: additionalContactIds,
      mapCenterLat: center ? center.lat() : undefined,
      mapCenterLng: center ? center.lng() : undefined,
      mapZoom: zoom,
      basemap,
      zones: payloadZones,
    });

    // Session 23: fail loudly. Silent-swallow was hiding real backend
    // errors (see zone-not-persisting bug). If the save returned an
    // error envelope, surface it as a toast so the rep knows the save
    // did NOT succeed and can act on the message.
    if (!res || !("id" in (res as any))) {
      const errMsg =
        (res as any)?.error ||
        (res as any)?.message ||
        "Save failed. Please try again.";
      // eslint-disable-next-line no-console
      console.error("handleSave failed", { res, sent: { locationId: resolvedLocationId, mapGroupId: sessionMapGroupId, zoneCount: sentZoneCount } });
      toast.add(errMsg, "red");
      return;
    }

    // Session 23: verify server-side integrity. If the backend created
    // a different mapGroup than the one we sent (fork-on-save bug
    // class), or persisted a different zone count than we sent, warn
    // aggressively — the rep is about to walk away thinking their edit
    // stuck when in fact something upstream is misrouting the write.
    const saved: any = res;
    const savedZoneCount = Array.isArray(saved.zones) ? saved.zones.length : 0;
    // eslint-disable-next-line no-console
    console.log("handleSave:response", {
      savedId: saved?.id,
      savedMapGroupId: saved?.mapGroupId,
      savedVersion: saved?.version,
      savedZoneCount,
      sentZoneCount,
      savedZones: Array.isArray(saved.zones)
        ? saved.zones.map((z: any) => ({
            id: z.id,
            name: z.name,
            sortOrder: z.sortOrder,
            pathVerts: z.path?.length ?? 0,
          }))
        : null,
    });
    if (saved.mapGroupId && saved.mapGroupId !== sessionMapGroupId) {
      // eslint-disable-next-line no-console
      console.warn("handleSave mapGroup mismatch", {
        sent: sessionMapGroupId,
        received: saved.mapGroupId,
      });
      toast.add(
        "Saved, but to a different map than expected. Refreshing.",
        "orange",
      );
    }
    if (savedZoneCount !== sentZoneCount) {
      // eslint-disable-next-line no-console
      console.warn("handleSave zone count mismatch", {
        sent: sentZoneCount,
        received: savedZoneCount,
      });
      toast.add(
        `Saved ${savedZoneCount} of ${sentZoneCount} zones. Check the map before continuing.`,
        "orange",
      );
    }

    setDirty(false);

    // WO-return mode (WO_MULCH_CALCULATOR_PATHWAY.md): the map is saved; now
    // push the measured total back onto the originating work order and hop
    // back to it, instead of the usual canonical-URL redirect. Returns true
    // when it handled navigation so we don't also run the canonical hop.
    if (returnToWoId) {
      const handled = await returnToWorkOrderAfterSave(saved?.id ?? null);
      if (handled) return;
    }

    // If we're on a non-canonical URL (alias or ad-hoc) at save time,
    // hop over to canonical so the URL stops being ambiguous.
    if (routeMapGroupId !== sessionMapGroupId) {
      navigate(
        `/mulch-calculator/${resolvedLocationId}/maps/${sessionMapGroupId}/edit`,
        { replace: true },
      );
      return;
    }

    refetch();
  };

  /**
   * Save-as-new-map — fork the current polygon set into a fresh Map at the
   * same Location. New mapGroupId, prompted-for name, none of the current
   * Map's versions are demoted. After success, we navigate to the new Map's
   * canonical URL so subsequent edits land there.
   */
  const handleSaveAsNewMap = async () => {
    if (!resolvedLocationId) return;
    if (totalCuYd === 0) return;

    const suggested =
      mapName && mapName !== "Untitled map" ? `${mapName} (copy)` : "";
    const requested = window.prompt(
      "Name for the new map (leave blank for \"Untitled map\"):",
      suggested,
    );
    // Prompt returns null when the user hits Cancel — bail without saving.
    if (requested === null) return;
    const newName = requested.trim() || "Untitled map";

    const forkedGroupId = newMapGroupId();
    const center = mapInstanceRef.current?.getCenter?.();
    const zoom = mapInstanceRef.current?.getZoom?.();

    const res = await save({
      locationId: resolvedLocationId,
      mapGroupId: forkedGroupId,
      name: newName,
      clientId: assignedClientId,
      // Session 25 Batch #8: fork carries primary + additional contacts
      // forward onto the new mapGroup. Reps can adjust after the fork
      // if the copy should have a different roster.
      primaryClientId: primaryContactId,
      contactIds: additionalContactIds,
      mapCenterLat: center ? center.lat() : undefined,
      mapCenterLng: center ? center.lng() : undefined,
      mapZoom: zoom,
      basemap,
      zones: zones.map((z, i) => ({
        name: z.name,
        color: z.color,
        depthInches: z.depthInches,
        areaSqMeters: z.areaSqMeters,
        path: z.canonicalPath,
        displayOffsets: z.displayOffsets,
        sortOrder: i,
      })),
    });

    // Session 23: same fail-loud pattern as handleSave.
    if (!res || !("id" in (res as any))) {
      const errMsg =
        (res as any)?.error ||
        (res as any)?.message ||
        "Save failed. Please try again.";
      // eslint-disable-next-line no-console
      console.error("handleSaveAsNewMap failed", { res });
      toast.add(errMsg, "red");
      return;
    }

    // KNOWN_ISSUES #15 — a fork saved from WO-return mode used to navigate
    // straight to the new map's canonical URL, dropping the work order
    // context entirely: no quantity push, no link, and the operator landed
    // somewhere they had not asked to be. "Save as new map" from a work
    // order is still a measurement OF that work order, so it settles back
    // the same way a normal save does.
    if (returnToWoId) {
      const handled = await returnToWorkOrderAfterSave((res as any)?.id ?? null);
      if (handled) return;
    }

    // Navigate to the new Map's canonical URL. The URL change triggers a
    // fresh mount — useMulchMap picks up the new mapGroupId + refetches.
    navigate(
      `/mulch-calculator/${resolvedLocationId}/maps/${forkedGroupId}/edit`,
      { replace: false },
    );
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (mapsError) {
    return (
      <div className={styles.placeholderPage}>
        <h2>Google Maps failed to load</h2>
        <p>{mapsError}</p>
      </div>
    );
  }

  if (bootstrapError) {
    return (
      <div className={styles.placeholderPage}>
        <h2>Couldn&apos;t open the editor</h2>
        <p>{bootstrapError}</p>
        <button
          className={styles.btnSecondary}
          onClick={() => navigate("/mulch-calculator")}
          style={{ marginTop: 16 }}
        >
          Back to calculator
        </button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backLink}
            onClick={() => {
              if (dirty) {
                const ok = window.confirm(
                  "You have unsaved changes. Leave anyway?",
                );
                if (!ok) return;
              }
              if (returnToWoId) {
                navigate(`/maintenance/work-orders/${returnToWoId}`);
              } else if (resolvedLocationId) {
                navigate(`/locations/${resolvedLocationId}`);
              } else {
                navigate("/mulch-calculator");
              }
            }}
          >
            ← {returnToWoId ? "Back to work order" : resolvedLocationId ? "Back to site" : "Back to calculator"}
          </button>
          {editingMapName ? (
            <input
              className={styles.pageTitleInput}
              value={mapNameDraft}
              autoFocus
              onChange={(e) => setMapNameDraft(e.target.value)}
              onBlur={commitMapNameEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitMapNameEdit();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setEditingMapName(false);
                }
              }}
              maxLength={150}
              placeholder="Map name"
            />
          ) : (
            <h1
              className={`${styles.pageTitle} ${styles.pageTitleEditable}`}
              title="Click to rename"
              onClick={() => {
                setMapNameDraft(mapName);
                setEditingMapName(true);
              }}
            >
              {mapName || "Untitled map"}
            </h1>
          )}
          <p className={styles.pageSubtitle}>
            {savedMap?.location?.address?.line1 ||
              savedMap?.location?.name ||
              initialAddress ||
              (bootstrapping ? "Preparing site…" : "New site")}
            {savedMap
              ? ` · last edited ${new Date(savedMap.updatedAt).toLocaleString()}`
              : " · draft — not saved yet"}
          </p>
          {returnToWoId && (
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 12,
                fontFamily: "Poppins",
                color: "#085041",
                background: "#E1F5EE",
                border: "1px solid #B6E2D3",
                borderRadius: 6,
                padding: "6px 10px",
                display: "inline-block",
              }}
            >
              Measuring for a work order. Save writes the total ({formatNumber(totalCuYd, 1)} cu yd) back to the work order as its quantity, and links this map to it.
            </p>
          )}
          {/* KNOWN_ISSUES #17 — arrived here WITHOUT work order context, but
              this site has live work. Advisory only: it never blocks
              measuring, and it offers the work order as a destination
              because that is where the measurement is actually needed. */}
          {!returnToWoId && siteWorkOrders.length > 0 && (
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 12,
                fontFamily: "Poppins",
                color: "#633806",
                background: "#FFF8E1",
                border: "1px solid #E7CC7A",
                borderRadius: 6,
                padding: "6px 10px",
                display: "inline-block",
              }}
            >
              {linkedWorkOrder ? (
                <>
                  This map is linked to work order{" "}
                  {linkedWorkOrder.externalId || linkedWorkOrder.name || "(unnamed)"}.
                </>
              ) : siteWorkOrders.length === 1 ? (
                <>
                  There is an open work order at this site:{" "}
                  {siteWorkOrders[0].externalId || siteWorkOrders[0].name || "(unnamed)"}.
                </>
              ) : (
                <>There are {siteWorkOrders.length} open work orders at this site.</>
              )}{" "}
              <button
                type="button"
                onClick={() =>
                  navigate(
                    linkedWorkOrder
                      ? `/maintenance/work-orders/${linkedWorkOrder.id}`
                      : siteWorkOrders.length === 1
                      ? `/maintenance/work-orders/${siteWorkOrders[0].id}`
                      : // MANY work orders at this site. Staff go to the
                        // Review queue filtered to the site; a CLIENT cannot
                        // — that route is isCompany and returned a bare 403,
                        // from a banner we put in front of them. Their own
                        // site page lists the same work orders, scoped to
                        // their org.
                        isStaff
                      ? `/maintenance/work-orders?locationId=${resolvedLocationId}`
                      : `/locations/${resolvedLocationId}`,
                  )
                }
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  fontFamily: "Poppins",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#185FA5",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {linkedWorkOrder || siteWorkOrders.length === 1
                  ? "Open the work order"
                  : "See them"}
              </button>
            </p>
          )}
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.historyLink}
            onClick={() => {
              if (!resolvedLocationId || !sessionMapGroupId) {
                alert(
                  "History is available after this map is saved to a site.",
                );
                return;
              }
              setHistoryModalOpen(true);
            }}
          >
            History
          </button>
          {/* OPERATOR-ONLY HEADER ACTIONS (Session 78). Export and
              Snapshots hit staff-only routes, and "Use for new RFQ"
              needs a product the client mode never loads — it would
              render permanently disabled with "No mulch product
              available", which reads as broken rather than as not-for-you.
              History stays: the versions read is client-scoped now, and
              seeing their own earlier measurements is useful.

              A client raises work from the work order or the site page,
              where the plan routing and the deposit rail already live.
              Starting an RFQ from inside the map would be a second,
              unscoped way in. */}
          {isStaff && (
            <>
          <button
            className={styles.historyLink}
            onClick={async () => {
              if (!savedMap?.id) {
                alert("Save this map before exporting.");
                return;
              }
              const payload = await exportMulchMap(savedMap.id);
              if (!payload) {
                alert("Export failed. Try again.");
                return;
              }
              const site = safeFilename(
                savedMap.location?.name ||
                  savedMap.location?.address?.line1 ||
                  "site",
              );
              const mapNameSafe = safeFilename(mapName || "map");
              downloadJson(`${site}__${mapNameSafe}__v${savedMap.version}.json`, payload);
            }}
          >
            Export
          </button>
          <button
            className={styles.historyLink}
            onClick={() => {
              if (!savedMap?.id) {
                alert(
                  "Snapshots are available after this map is saved to a site.",
                );
                return;
              }
              setSnapshotsOpen(true);
            }}
          >
            Snapshots
          </button>
          <button
            className={styles.btnPrimary}
            onClick={() => setRfqModalOpen(true)}
            disabled={totalCuYd === 0 || !productId || productLoading}
            title={
              totalCuYd === 0
                ? "Draw at least one zone first"
                : productLoading
                  ? "Loading product…"
                  : !productId
                    ? productError || "No mulch product available"
                    : undefined
            }
          >
            Use for new RFQ
          </button>
            </>
          )}
        </div>
      </header>

      <div className={styles.workspace}>
        <div className={styles.mapPane}>
          {!mapReady && (
            <div className={styles.mapPlaceholder}>
              <p>
                {bootstrapping || chooserMaps
                  ? "Preparing site…"
                  : "Loading map…"}
              </p>
            </div>
          )}
          <div
            ref={mapDivRef}
            className={styles.map}
            style={{ display: is3DMode ? "none" : undefined }}
          />
          <div
            ref={map3DContainerRef as React.RefObject<HTMLDivElement>}
            className={styles.map3d}
            style={{ display: is3DMode ? undefined : "none" }}
          />

          {mapReady && (
            <>
              <div className={styles.mapTopBar}>
                <div className={styles.modeToggle}>
                  <button
                    className={`${styles.modeBtn} ${
                      !is3DMode ? styles.modeBtnActive : ""
                    }`}
                    onClick={() => is3DMode && toggle3D()}
                  >
                    2D
                  </button>
                  <button
                    className={`${styles.modeBtn} ${
                      is3DMode ? styles.modeBtnActive : ""
                    }`}
                    disabled={!is3DAvailable}
                    onClick={() => !is3DMode && toggle3D()}
                  >
                    3D
                  </button>
                </div>
                <span className={styles.modeHint}>
                  Polygons sync live in both
                </span>
              </div>

              {!is3DMode && (
                <div className={styles.basemapPicker}>
                  <button
                    className={styles.basemapTrigger}
                    onClick={() => setBasemapMenuOpen((v) => !v)}
                    title="Switch satellite provider"
                  >
                    <span>Basemap:</span>{" "}
                    <strong>
                      {BASEMAPS.find((b) => b.id === basemap)?.label}
                    </strong>
                    <span className={styles.basemapCaret}>▾</span>
                  </button>
                  {basemapMenuOpen && (
                    <div className={styles.basemapMenu}>
                      {BASEMAPS.map((opt) => {
                        const missingToken =
                          opt.id === "mapbox_sat" &&
                          !process.env.REACT_APP_MAPBOX_TOKEN;
                        return (
                          <button
                            key={opt.id}
                            className={`${styles.basemapMenuItem} ${
                              basemap === opt.id
                                ? styles.basemapMenuItemActive
                                : ""
                            }`}
                            disabled={missingToken}
                            onClick={() => {
                              if (missingToken) return;
                              setBasemap(opt.id);
                              setBasemapMenuOpen(false);
                            }}
                          >
                            <span>{opt.label}</span>
                            <span className={styles.basemapAttribution}>
                              {missingToken ? "needs Mapbox token" : opt.attribution}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className={styles.drawToolbar}>
                {!isDrawing ? (
                  <button
                    className={styles.drawBtn}
                    onClick={beginDraw}
                    disabled={zones.length >= MAX_ZONES || is3DMode}
                    title="Draw zone"
                  >
                    Draw
                  </button>
                ) : (
                  <>
                    <button
                      className={`${styles.drawBtn} ${styles.drawBtnActive}`}
                      onClick={cancelDraw}
                      title="Cancel"
                    >
                      Stop
                    </button>
                    {drawingVertexCount > 0 && (
                      <button
                        className={styles.drawBtn}
                        onClick={undoLastVertex}
                        title="Undo last point (Backspace)"
                      >
                        Undo
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className={styles.mapBottomBar}>
                <span>Aerial imagery date not yet available</span>
                <span>
                  {isDrawing
                    ? drawingVertexCount === 0
                      ? "Click first point"
                      : drawingVertexCount < 3
                        ? `${drawingVertexCount} point${
                            drawingVertexCount > 1 ? "s" : ""
                          }`
                        : `${drawingVertexCount} points — click first vertex, dbl-click, or Enter to close`
                    : ""}
                </span>
              </div>
            </>
          )}
        </div>

        <aside className={styles.sidebar}>
          <div className={styles.sidebarSection}>
            <p className={styles.sectionLabel}>Site</p>
            <p className={styles.siteName}>
              {savedMap?.name ||
                savedMap?.location?.name ||
                initialAddress ||
                "New map"}
            </p>
            {savedMap?.location?.address?.line1 && (
              <p className={styles.siteAddress}>
                {savedMap.location.address.line1}
              </p>
            )}
          </div>

          <div className={styles.sidebarSection}>
            <div className={styles.serviceHeader}>
              <p className={styles.sectionLabel} style={{ margin: 0 }}>
                Zones
              </p>
              <span>
                {zoneRollup.length} · {formatNumber(totalCuYd, 1)} cu yd
              </span>
            </div>
            {zoneRollup.length === 0 ? (
              <div className={styles.emptyZones}>
                No zones yet. Click <strong>Draw</strong> and trace an area on
                the map.
              </div>
            ) : (
              <div
                className={styles.zoneList}
                style={zoneRollup.length > 3 ? { maxHeight: 380, overflowY: "auto" } : undefined}
              >
                {zoneRollup.map((zone) => (
                  <div
                    key={zone.id}
                    className={styles.zoneCard}
                    style={
                      { ["--zone-color" as any]: zone.color } as React.CSSProperties
                    }
                  >
                    <div className={styles.zoneCardHeader}>
                      {editingNameId === zone.id ? (
                        <input
                          className={styles.zoneNameInput}
                          value={editNameValue}
                          autoFocus
                          onChange={(e) => setEditNameValue(e.target.value)}
                          onBlur={() => finishEditName(zone.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") finishEditName(zone.id);
                            if (e.key === "Escape") setEditingNameId(null);
                          }}
                        />
                      ) : (
                        <span
                          className={styles.zoneName}
                          onClick={() => startEditName(zone)}
                        >
                          {zone.name}
                        </span>
                      )}
                      <label
                        className={styles.zoneColorSwatch}
                        style={{ background: zone.color }}
                        title="Change zone color"
                      >
                        <input
                          type="color"
                          value={toHexColor(zone.color)}
                          onChange={(e) =>
                            updateZoneColor(zone.id, e.target.value)
                          }
                          style={{
                            position: "absolute",
                            width: 0,
                            height: 0,
                            opacity: 0,
                            pointerEvents: "none",
                          }}
                        />
                      </label>
                      <button
                        className={styles.zoneDelete}
                        onClick={() => deleteZone(zone.id)}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>
                    <div className={styles.zoneYards}>
                      {formatNumber(zone.cubicYards, 1)} yd
                    </div>
                    <div className={styles.zoneSqFt}>
                      {formatNumber(zone.areaSqFt)} sq ft
                    </div>
                    <div className={styles.depthRow}>
                      <span className={styles.depthLabel}>Depth</span>
                      {DEPTH_PRESETS.map((p) => (
                        <button
                          key={p}
                          className={`${styles.depthBtn} ${
                            zone.depthInches === p ? styles.depthBtnActive : ""
                          }`}
                          onClick={() => updateDepth(zone.id, p)}
                        >
                          {p}
                        </button>
                      ))}
                      <div className={styles.depthCustom}>
                        <input
                          type="number"
                          min={1}
                          max={24}
                          value={zone.depthInches}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (v >= 1 && v <= 24) updateDepth(zone.id, v);
                          }}
                        />
                        <span>in</span>
                      </div>
                    </div>
                    {canNudge && nudgingZoneId !== zone.id && (
                      <button
                        className={styles.zoneAdjustLink}
                        onClick={() => startNudge(zone.id)}
                      >
                        Adjust position for{" "}
                        {is3DMode
                          ? "Google 3D"
                          : BASEMAPS.find((b) => b.id === basemap)?.label}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {availability?.services?.length ? (
            <div className={styles.sidebarSection}>
              <div className={styles.serviceHeader}>
                <p className={styles.sectionLabel} style={{ margin: 0 }}>
                  Service type
                </p>
                <span>Available in this area</span>
              </div>
              {availability.services.map((svc) => (
                <label key={svc.id} className={styles.serviceRow}>
                  <span>
                    <input
                      type="radio"
                      name="serviceType"
                      checked={selectedServiceId === svc.id}
                      onChange={() => setSelectedServiceId(svc.id)}
                    />
                    {svc.name}
                  </span>
                  <span className={styles.serviceProvider}>
                    {svc.providers[0]?.name || "—"}
                  </span>
                </label>
              ))}
            </div>
          ) : null}

          {availableAddOns.length > 0 && (
            <div className={styles.sidebarSection}>
              <p className={styles.sectionLabel}>Add-ons</p>
              {availableAddOns.map((ao) => (
                <label key={ao.id} className={styles.serviceRow}>
                  <span>
                    <input
                      type="checkbox"
                      checked={selectedAddOnIds.includes(ao.id)}
                      onChange={(e) =>
                        setSelectedAddOnIds((prev) =>
                          e.target.checked
                            ? [...prev, ao.id]
                            : prev.filter((id) => id !== ao.id),
                        )
                      }
                    />
                    {ao.name}
                  </span>
                  {ao.description && (
                    <span className={styles.serviceProvider}>
                      {ao.description}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}

          <div className={styles.sidebarSection}>
            <p className={styles.sectionLabel}>Total</p>
            <div className={styles.totalsBlock}>
              <div className={styles.totalsHeadline}>
                <strong>{formatNumber(totalCuYd, 1)} cu yd</strong>
                <span>{formatNumber(totalSqFt)} sq ft</span>
              </div>
              {isStaff && (estimate ? (
                <>
                  <div className={styles.totalsLine}>
                    <span>Cost</span>
                    <strong>{formatCurrency(estimate.totals.cost)}</strong>
                  </div>
                  <div className={styles.totalsLine}>
                    <span>Retail</span>
                    <strong>{formatCurrency(estimate.totals.price)}</strong>
                  </div>
                  <div className={styles.totalsDivider} />
                  <div className={styles.totalsMargin}>
                    <span>Margin</span>
                    <strong>
                      {formatCurrency(estimate.totals.margin)} ·{" "}
                      {estimate.totals.marginPercent.toFixed(1)}%
                    </strong>
                  </div>
                  <p className={styles.rateNote}>
                    {estimate.service.name}: $
                    {estimate.service.perUnitPrice.toFixed(2)}/yd retail
                  </p>
                </>
              ) : estimateError ? (
                // Surface the real reason the estimate failed instead of a
                // permanent "Computing…". useEstimate captures the backend
                // message (e.g. "no provider offers this product + service
                // at this location"); previously it was dropped and the
                // operator just saw a blank price.
                <p
                  style={{
                    fontSize: 11,
                    color: "#C05621",
                    margin: "8px 0 0",
                  }}
                >
                  {estimateError}
                </p>
              ) : (
                <p
                  style={{
                    fontSize: 11,
                    color: "#717D96",
                    margin: "8px 0 0",
                  }}
                >
                  {productLoading
                    ? "Loading product…"
                    : !productId
                      ? productError || "No mulch product available."
                      : zones.length === 0
                        ? "Draw a zone to see cost/retail."
                        : estimateComputing
                          ? "Computing…"
                          : "Draw a zone to see cost/retail."}
                </p>
              ))}
            </div>
          </div>

          {/* Session 27 (revision 2) — Assigned to moved below Total,
              above Contacts. Configuration (client + contacts) lives
              at the bottom of the sidebar; zones/service/total lead.

              STAFF ONLY: the picker lists the company's clients, and
              "internal draft" is an operator concept. A client measuring
              their own site has nothing to assign. */}
          {isStaff && (
          <div className={styles.sidebarSection}>
            <p className={styles.sectionLabel}>Assigned to</p>
            <SelectInput
              name="assignedClient"
              options={clientOptions}
              value={assignedClientId ?? ""}
              searchable
              clearable
              disabled={clientsLoading}
              onChange={(v: any) => {
                const next = v ? String(v) : null;
                if (next !== assignedClientId) {
                  setAssignedClientId(next);
                  // Session 25 Batch #8: primary + additional contacts
                  // belong to a specific client organization. Changing
                  // the client invalidates any previously-picked
                  // contacts (backend guardrail would reject them at
                  // save time). Clear both to keep the sidebar in a
                  // valid state and avoid a save-time BadRequest.
                  setPrimaryContactId(null);
                  setAdditionalContactIds([]);
                  setDirty(true);
                }
              }}
            />
            <p style={{ fontSize: 10, color: "#717D96", margin: "6px 0 0" }}>
              {assignedClientId
                ? matchingProfile
                  ? "Existing profile for this client at this site — last updated " + new Date(matchingProfile.updatedAt).toLocaleDateString() + "."
                  : "New profile will be created for this client at this site on save."
                : "Internal draft — not assigned to any client."}
            </p>
            {profilesAtSite.length > 0 && !assignedClientId && (
              <p
                style={{
                  fontSize: 10,
                  color: "#5D5E62",
                  fontStyle: "italic",
                  margin: "4px 0 0",
                }}
              >
                This site has {profilesAtSite.length} existing profile
                {profilesAtSite.length === 1 ? "" : "s"}:{" "}
                {profilesAtSite
                  .slice(0, 3)
                  .map((p) => p.clientName || "Unknown")
                  .join(", ")}
                {profilesAtSite.length > 3
                  ? ", +" + (profilesAtSite.length - 3) + " more"
                  : ""}
                .
              </p>
            )}
          </div>
          )}

          {/* Session 27 — collapsible Contacts section, positioned
              below Total. Groups per-version primary/additional/site
              contact overrides so calc stays visually dominant. */}
          <div className={styles.sidebarSection}>
            <button
              type="button"
              onClick={() => setContactsExpanded((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
                color: "inherit",
                font: "inherit",
              }}
              title={contactsExpanded ? "Hide contacts" : "Show contacts"}
            >
              <span className={styles.sectionLabel} style={{ margin: 0 }}>
                Contacts
                {(() => {
                  const count =
                    (primaryContactId ? 1 : 0) +
                    additionalContactIds.length +
                    ((siteContactName.trim() || siteContactEmail.trim() || siteContactPhone.trim()) ? 1 : 0);
                  return count > 0 ? (
                    <span style={{ marginLeft: 6, fontWeight: 400, color: "#717D96" }}>
                      · {count} set
                    </span>
                  ) : null;
                })()}
              </span>
              <span style={{ color: "#717D96", fontSize: 12 }}>
                {contactsExpanded ? "▾" : "▸"}
              </span>
            </button>
            {contactsExpanded && (
              <div style={{ marginTop: 12 }}>
                {/* Primary + Additional (per-version) — client-scoped */}
                {assignedClientId && (
                  <div style={{ marginBottom: 20 }}>
                    <p className={styles.sectionLabel}>
                      Primary contact (this version)
                    </p>
                    <SelectInput
                      name="primaryContact"
                      options={assignableContacts.map((c) => ({
                        value: c.id,
                        label: c.name || c.contactEmail || c.id,
                      }))}
                      value={primaryContactId ?? ""}
                      searchable
                      clearable
                      disabled={contactsLoading}
                      onChange={(v: any) => {
                        const next = v ? String(v) : null;
                        if (next !== primaryContactId) {
                          setPrimaryContactId(next);
                          if (next && additionalContactIds.includes(next)) {
                            setAdditionalContactIds(
                              additionalContactIds.filter((id) => id !== next),
                            );
                          }
                          setDirty(true);
                        }
                      }}
                    />
                    <p style={{ fontSize: 10, color: "#717D96", margin: "6px 0 0" }}>
                      {contactsLoading
                        ? "Loading contacts…"
                        : assignableContacts.length === 0
                          ? "No contacts available for this client."
                          : "The org's point person for this version. Overrides the profile default when this map is attached to a downstream record."}
                    </p>

                    <p className={styles.sectionLabel} style={{ marginTop: 16 }}>
                      Additional contacts (this version)
                    </p>
                    <SelectInput
                      name="additionalContacts"
                      multiple
                      options={assignableContacts
                        .filter((c) => c.id !== primaryContactId)
                        .map((c) => ({
                          value: c.id,
                          label: c.name || c.contactEmail || c.id,
                        }))}
                      value={additionalContactIds}
                      searchable
                      clearable
                      disabled={contactsLoading}
                      onChange={(v: any) => {
                        const next: string[] = Array.isArray(v)
                          ? v.map((x: any) => String(x))
                          : v
                            ? [String(v)]
                            : [];
                        const same =
                          next.length === additionalContactIds.length &&
                          next.every((id) => additionalContactIds.includes(id));
                        if (!same) {
                          setAdditionalContactIds(next);
                          setDirty(true);
                        }
                      }}
                    />
                    <p style={{ fontSize: 10, color: "#717D96", margin: "6px 0 0" }}>
                      Additional CC recipients on downstream RFQs / Quotes /
                      Projects when this map is attached.
                    </p>
                  </div>
                )}

                {/* Site contact (per-version) — always available */}
                <div>
                  <p className={styles.sectionLabel}>Site contact (this version)</p>
                  <p style={{ fontSize: 10, color: "#717D96", margin: "0 0 6px" }}>
                    Overrides the client&apos;s default for this specific version.
                    Leave blank to use the client&apos;s site contact.
                  </p>
                  <input
                    type="text"
                    placeholder="Name"
                    value={siteContactName}
                    onChange={(e) => {
                      setSiteContactName(e.target.value);
                      setDirty(true);
                    }}
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      fontSize: 12,
                      border: "1px solid #E4E7EB",
                      borderRadius: 4,
                      marginBottom: 6,
                      boxSizing: "border-box",
                    }}
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={siteContactEmail}
                    onChange={(e) => {
                      setSiteContactEmail(e.target.value);
                      setDirty(true);
                    }}
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      fontSize: 12,
                      border: "1px solid #E4E7EB",
                      borderRadius: 4,
                      marginBottom: 6,
                      boxSizing: "border-box",
                    }}
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={siteContactPhone}
                    onChange={(e) => {
                      setSiteContactPhone(e.target.value);
                      setDirty(true);
                    }}
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      fontSize: 12,
                      border: "1px solid #E4E7EB",
                      borderRadius: 4,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className={styles.saveRow}>
            <span className={styles.saveStatus}>
              {dirty ? "Unsaved changes" : "All changes saved"}
            </span>
            <button
              className={styles.btnSecondary}
              onClick={handleSaveAsNewMap}
              disabled={
                saving || !resolvedLocationId || totalCuYd === 0
              }
              title={
                totalCuYd === 0
                  ? "Draw at least one zone first"
                  : "Fork this design into a new map at the same site"
              }
            >
              Save as new map
            </button>
            <button
              className={styles.btnPrimary}
              onClick={handleSave}
              disabled={
                saving ||
                !resolvedLocationId ||
                !sessionMapGroupId ||
                totalCuYd === 0 ||
                (!dirty && !!savedMap)
              }
              title={
                !resolvedLocationId
                  ? "Preparing site…"
                  : totalCuYd === 0
                    ? "Draw at least one zone first"
                    : !savedMap
                      ? "Save creates the first version of this map"
                      : undefined
              }
            >
              {saving
                ? "Saving…"
                : savedMap
                  ? "Save"
                  : "Save first version"}
            </button>
          </div>
        </aside>
      </div>

      {historyModalOpen && resolvedLocationId && sessionMapGroupId && (
        <VersionHistoryModal
          locationId={resolvedLocationId}
          mapGroupId={sessionMapGroupId}
          currentMapId={savedMap?.id || null}
          onClose={() => setHistoryModalOpen(false)}
          onRestored={() => {
            navigate(0);
          }}
        />
      )}

      {chooserMaps && (
        <ExistingMapsChooserModal
          address={initialAddress}
          maps={chooserMaps}
          onOpen={handleChooserOpen}
          onStartNew={handleChooserStartNew}
          onCancel={handleChooserCancel}
        />
      )}

      {nudgingZone && (
        <NudgePanel
          zoneName={nudgingZone.name}
          is3D={is3DMode}
          offset={{
            dx:
              (nudgingZone.displayOffsets[activeBasemap]?.dx || 0) +
              nudgePending.dx,
            dy:
              (nudgingZone.displayOffsets[activeBasemap]?.dy || 0) +
              nudgePending.dy,
          }}
          onSave={saveNudge}
          onReset={resetNudge}
          onCancel={cancelNudge}
        />
      )}

      {rfqModalOpen && (
        <CreateRfqModal
          locationId={resolvedLocationId}
          mapGroupId={sessionMapGroupId}
          mapName={mapName}
          address={
            savedMap?.location?.address ||
            (initialAddress ? { line1: initialAddress } : null)
          }
          latitude={
            savedMap?.location?.latitude ??
            (mapInstanceRef.current?.getCenter?.()?.lat?.() ?? initialLat)
          }
          longitude={
            savedMap?.location?.longitude ??
            (mapInstanceRef.current?.getCenter?.()?.lng?.() ?? initialLng)
          }
          productId={productId}
          totalCuYd={totalCuYd}
          totalSqFt={totalSqFt}
          zonesForSave={zones.map((z, i) => ({
            name: z.name,
            color: z.color,
            depthInches: z.depthInches,
            areaSqMeters: z.areaSqMeters,
            // Canonical GPS coords, not the display-offset polygon path.
            path: z.canonicalPath,
            displayOffsets: z.displayOffsets,
            sortOrder: i,
          }))}
          basemap={basemap}
          mapCenter={
            mapInstanceRef.current?.getCenter
              ? {
                  lat: mapInstanceRef.current.getCenter().lat(),
                  lng: mapInstanceRef.current.getCenter().lng(),
                }
              : null
          }
          mapZoom={mapInstanceRef.current?.getZoom?.() ?? null}
          dirty={dirty}
          onClose={() => setRfqModalOpen(false)}
        />
      )}

      {snapshotsOpen && (
        <MapSnapshotsModal
          mulchMapId={savedMap?.id ?? null}
          captureDisabledReason={null}
          captureNoticeText={
            is3DMode
              ? "3D capture uses your browser\u2019s screen-share \u2014 you\u2019ll get a prompt to share this tab. Only one frame is captured; nothing is recorded."
              : null
          }
          captureCanvas={async () => {
            // Session 28: client-side capture. Render whichever basemap
            // the rep is currently looking at (2D Google Hybrid / Mapbox
            // / Esri, or Google Photorealistic 3D) to a PNG Blob and
            // hand it to the modal. viewState is metadata only \u2014 the
            // backend logs it but doesn't persist it in v1.
            const blob = await captureMapAsBlob({
              is3D: is3DMode,
              mapDivRef,
              map3DContainerRef,
              mapInstanceRef,
            });
            if (!blob) return null;

            // Best-effort viewState. In 3D we prefer the 3D element's
            // camera reading; in 2D we read the Google Maps instance.
            let centerLat = 0;
            let centerLng = 0;
            let zoom = 0;
            let tilt: number | undefined;
            let heading: number | undefined;
            if (is3DMode) {
              const el: any =
                map3DContainerRef.current?.querySelector("gmp-map-3d");
              const c = el?.center;
              if (c) {
                centerLat = typeof c.lat === "function" ? c.lat() : c.lat;
                centerLng = typeof c.lng === "function" ? c.lng() : c.lng;
              }
              tilt = typeof el?.tilt === "number" ? el.tilt : undefined;
              heading =
                typeof el?.heading === "number" ? el.heading : undefined;
            } else {
              const m = mapInstanceRef.current;
              if (m && typeof m.getCenter === "function") {
                const c = m.getCenter();
                if (c) {
                  centerLat = c.lat();
                  centerLng = c.lng();
                }
                const z = m.getZoom?.();
                if (typeof z === "number") zoom = z;
              }
            }
            return {
              blob,
              viewState: {
                centerLat,
                centerLng,
                zoom,
                tilt,
                heading,
                basemap: activeBasemap,
              },
            };
          }}
          onClose={() => setSnapshotsOpen(false)}
        />
      )}
    </div>
  );
};

export default MapEditor;
